<!-- Extensibility (point 7): embed arbitrary HTML or a published PluginWidget
     bundle as a design element, in a sandboxed iframe.
     · kind "embed"  → props.html rendered directly (HtmlWidget equivalent).
     · kind "plugin" → props.bundle_id fetched via get_widget_bundle, its
       html_b64 decoded + config injected, then hosted via the widget-SDK so
       it gets a LIVE data plane (subscribe/history) and write-back.
     In EDIT mode the iframe is click-through (pointer-events:none) and inert
     (no host); in RUN mode it is interactive and the host is attached so the
     bundle receives data. Write-back (setVariable/command) is emitted up to the
     runtime view, which applies it (command honours "command mode"). -->
<template>
  <div class="syn-embed" :style="{ pointerEvents: interactive ? 'none' : 'auto' }">
    <!-- No loading="lazy": there is nothing to defer (srcdoc is already in the
         document, not a network fetch), and inside the design canvas — which is
         laid out at design coordinates and then CSS-scaled to fit — Chrome's
         viewport heuristic can decide the frame is far off-screen and never
         load it at all. The symptom is a widget that renders as an empty white
         rectangle until something re-assigns srcdoc. -->
    <iframe v-if="srcdoc" ref="frameRef" :srcdoc="srcdoc" sandbox="allow-scripts" referrerpolicy="no-referrer" :title="title" class="frame"></iframe>
    <div v-else class="ph">{{ phText }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, nextTick, ref, watch } from 'vue';
import { callTool } from '../api/client';
import { connectLiveData, type LiveDataConn } from './liveData';
import { createPluginWidgetHost, type DataBridge, type ContextProvider, type PluginWidgetHost } from './widget-sdk/host';
import type { WidgetManifest } from './widget-sdk/manifest';
import type { WidgetContext, DataQuery, DataResult } from './widget-sdk/protocol';

const props = withDefaults(defineProps<{
  kind: string;
  p: Record<string, any>;
  interactive?: boolean;
  // Data plane (run mode): a same-tenant embed token + API base for
  // POST /api/embed/data (binding resolution). In edit mode these are absent
  // and no host runs.
  dataBase?: string;
  dataToken?: string;
}>(), { interactive: false, dataBase: '', dataToken: '' });

const emit = defineEmits<{
  (e: 'set-var', name: string, value: unknown): void;
  (e: 'command', id: string, value: unknown): void;
}>();

const title = computed(() => (props.kind === 'plugin' ? (bundleName.value || 'Plugin') : 'HTML'));

// ── HTML embed ───────────────────────────────────────────────────────────
const htmlSrc = computed(() => (props.kind === 'embed' ? String(props.p.html ?? '') : ''));

// ── Plugin bundle ──────────────────────────────────────────────────────────
const frameRef = ref<HTMLIFrameElement | null>(null);
const bundleName = ref('');
const bundleHtml = ref('');
const bundleManifest = ref<WidgetManifest | null>(null);
const resolvedConfig = ref<Record<string, unknown>>({});
let host: PluginWidgetHost | null = null;

async function fetchBundleViaEmbed(id: string): Promise<{ name?: string; html_b64?: string; manifest?: WidgetManifest }> {
  const url = `${props.dataBase}/api/embed/widget-bundle/${encodeURIComponent(id)}?token=${encodeURIComponent(props.dataToken)}`;
  const r = await fetch(url);
  if (!r.ok) throw new Error(`widget-bundle ${r.status}`);
  return await r.json() as { name?: string; html_b64?: string; manifest?: WidgetManifest };
}

async function loadBundle(): Promise<void> {
  teardownHost();
  bundleHtml.value = '';
  bundleName.value = '';
  bundleManifest.value = null;
  const id = props.p.bundle_id;
  if (props.kind !== 'plugin' || !id) return;
  try {
    // Token-authed embed/kiosk (no user session) → the dedicated embed endpoint;
    // editor/Run mode (has a session) → the authed tool surface.
    const r = props.dataToken
      ? await fetchBundleViaEmbed(id)
      : await callTool<{ name?: string; html_b64?: string; manifest?: WidgetManifest }>('get_widget_bundle', { bundle_id: id });
    bundleName.value = r.name ?? '';
    bundleManifest.value = r.manifest ?? ({ name: r.name ?? 'widget', version: '0', sdk_version: '1.0' } as WidgetManifest);
    // RUN mode with a data plane → resolve binding inputs to their value so the
    // widget shows it (edit mode keeps the raw binding; nothing to resolve against).
    resolvedConfig.value = !props.interactive && props.dataToken
      ? await resolveConfigBindings(props.p.config)
      : ((props.p.config ?? {}) as Record<string, unknown>);
    bundleHtml.value = r.html_b64 ? buildPluginSrcdoc(r.html_b64, resolvedConfig.value) : '';
    await nextTick();
    attachHost();
    startBindingRefresh();
  } catch { bundleHtml.value = ''; }
}

// ── Live binding subscriptions (ADR 50) ─────────────────────────────────────
// ONE /api/live socket per iframe, shared by two consumers, both server-pushed
// on change (no polling):
//   (a) the config-input refresh — keeps binding-valued inputs live by
//       re-emitting { inputs:{name:value} } to the iframe (sub_id == config key);
//   (b) the SDK subscribeData (scalar) — pushes value deltas to the widget
//       (sub_id == "sdk_N"), via the dataBridge below.
let liveConn: LiveDataConn | null = null;
const liveSubs = new Map<string, { binding: unknown; onValue: (v: unknown) => void }>();
let sdkSubSeq = 0;

function liveBindings(): { sub_id: string; binding: unknown }[] {
  return [...liveSubs].map(([sub_id, s]) => ({ sub_id, binding: s.binding }));
}
function ensureLiveConn(): void {
  if (liveConn || props.interactive || !props.dataToken) return;
  liveConn = connectLiveData({
    token: props.dataToken,
    base: props.dataBase || undefined,
    variables: [],
    onSample: () => {},
    bindings: liveBindings(),
    onBindingValue: (m) => liveSubs.get(m.sub_id)?.onValue(m.value),
  });
}
function addLiveSub(subId: string, binding: unknown, onValue: (v: unknown) => void): void {
  liveSubs.set(subId, { binding, onValue });
  ensureLiveConn();
  liveConn?.setBindings(liveBindings());
}
function removeLiveSub(subId: string): void {
  if (!liveSubs.delete(subId)) return;
  if (liveSubs.size === 0) { liveConn?.close(); liveConn = null; }
  else liveConn?.setBindings(liveBindings());
}
function stopAllLive(): void {
  liveSubs.clear();
  liveConn?.close();
  liveConn = null;
}
// Config-input subscriptions (re)start whenever the bundle (re)loads. SDK subs
// (sdk_*) are owned by the widget/host and dropped on teardown via stopAllLive.
function stopBindingRefresh(): void {
  for (const k of [...liveSubs.keys()]) if (!k.startsWith('sdk_')) liveSubs.delete(k);
  if (liveSubs.size === 0) { liveConn?.close(); liveConn = null; }
  else liveConn?.setBindings(liveBindings());
}
function startBindingRefresh(): void {
  stopBindingRefresh();
  if (props.interactive || !props.dataToken) return;
  const config = (props.p.config ?? {}) as Record<string, unknown>;
  for (const k of bindingKeys(config)) {
    addLiveSub(k, config[k], (value) => frameRef.value?.contentWindow?.postMessage({ inputs: { [k]: value } }, '*'));
  }
}

function buildPluginSrcdoc(htmlB64: string, config: unknown): string {
  let body: string;
  try { body = decodeURIComponent(escape(atob(htmlB64))); } catch { body = atob(htmlB64); }
  // Point the SDK import at this origin (served from web/public/widget-sdk).
  const sdkUrl = `${window.location.origin}/widget-sdk/v1/client.js`;
  body = body.replace(/https:\/\/cdn\.lymon\.io\/widget-sdk\/v1\/client\.js/g, sdkUrl);
  const cfgB64 = btoa(unescape(encodeURIComponent(JSON.stringify(config ?? {}))));
  const cfg = `<script>window.__LYMON_CONFIG_B64__=${JSON.stringify(cfgB64)};<\/script>`;
  return body.includes('</head>') ? body.replace('</head>', `${cfg}</head>`) : cfg + body;
}

// ── Data plane (run mode only) ──────────────────────────────────────────
// One endpoint: POST /api/embed/data resolves a BINDING (any source +
// transforms, the same object a native element stores) via the native render
// path. The plugin hands us the binding; we just add the embed token.
async function fetchData(query: DataQuery): Promise<DataResult> {
  const url = `${props.dataBase}/api/embed/data?token=${encodeURIComponent(props.dataToken)}`;
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      binding: query.binding,
      time_range: query.timeRange,
      tz: query.tz,
      want_series: query.wantSeries,
      want_table: query.wantTable,
    }),
  });
  if (!r.ok) throw new Error(`embed/data ${r.status}`);
  return await r.json() as DataResult;
}

// The latest scalar of a data result (a config input wants a value, not a series).
function scalarOf(r: DataResult): unknown {
  if (!r || r.ok === false) return undefined;
  if (r.value !== undefined && r.value !== null) return r.value;
  const pts = r.series?.[0]?.points;
  if (pts && pts.length) return pts[pts.length - 1]?.value ?? undefined;
  return undefined;
}

// ADR 41/widget-SDK — auto-resolve binding-typed config inputs to their current
// value before handing config to the widget. A bundle whose manifest declares an
// input as binding/series-ref/connector-point stores a BINDING object in config;
// resolving it here means simple widgets (incl. AI-authored ones that read
// `config[name]` directly, without calling fetchData) show the live value. Series
// or per-point widgets can still call fetchData/subscribeData themselves.
// A config value is a DATA BINDING if it carries a binding signature — detect by
// SHAPE (not by manifest type), so it resolves even if the manifest is stale or
// the embed endpoint didn't return it.
function looksLikeBinding(v: unknown): v is Record<string, unknown> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  return typeof o.kind === 'string' || 'series_id' in o || 'metric_name' in o || 'connector_id' in o;
}
function bindingKeys(config: unknown): string[] {
  const o = (config ?? {}) as Record<string, unknown>;
  return Object.keys(o).filter((k) => looksLikeBinding(o[k]));
}
// Resolve ONLY the binding-valued keys → { key: currentValue }. Used both at
// load (baked into config) and on the refresh tick (pushed to the widget).
async function resolveBindingValues(config: unknown): Promise<Record<string, unknown>> {
  const src = (config ?? {}) as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  await Promise.all(
    bindingKeys(src).map(async (k) => {
      try {
        const s = scalarOf(await fetchData({ binding: src[k] } as DataQuery));
        if (s !== undefined) out[k] = s;
      } catch { /* best-effort */ }
    }),
  );
  return out;
}
async function resolveConfigBindings(config: unknown): Promise<Record<string, unknown>> {
  const out = { ...((config ?? {}) as Record<string, unknown>) };
  Object.assign(out, await resolveBindingValues(config));
  return out;
}

const dataBridge: DataBridge = {
  fetchData,
  // ADR 50 — scalar live subscription over WS (the host calls this instead of
  // polling fetchData). Returns an unsubscribe fn. Delivers the value as a
  // DataResult so the widget's data-value handler is unchanged.
  subscribeData(query, onValue) {
    const subId = `sdk_${++sdkSubSeq}`;
    addLiveSub(subId, (query as { binding?: unknown }).binding, (value) => onValue({ ok: true, value } as DataResult));
    return () => removeLiveSub(subId);
  },
  async invokeCommand(id, args) {
    // A design "command" = a process write-back. Bubble it to the runtime
    // view, which gates it on command mode and calls control_write.
    emit('command', id, args);
    return null;
  },
  async setVariable(name, value) {
    emit('set-var', name, value);
  },
  async evalExpression() { return null; },
};

// tenant_id for storage namespacing + context (informational only). Best-effort
// decode of the embed JWT payload — NEVER trusted for authorization (the gateway
// resolves the real tenant from the verified token on every /api/embed/data call).
function tenantFromToken(tok: string): string {
  try {
    const payload = tok.split('.')[1];
    if (!payload) return 'unknown';
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { tenant_id?: string };
    return claims.tenant_id || 'unknown';
  } catch { return 'unknown'; }
}
const tenantId = computed(() => tenantFromToken(props.dataToken));

const contextProvider: ContextProvider = {
  current(): Omit<WidgetContext, 'protocol'> {
    return { vars: {}, timeRange: null, theme: themeVars(), locale: navigator.language || 'en-US', tenant_id: tenantId.value };
  },
  onChange() { return () => {}; },
};

function themeVars(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const cs = getComputedStyle(document.documentElement);
  const pick = (n: string): string => cs.getPropertyValue(n).trim();
  return { foreground: pick('--color-ink') || '#111', background: pick('--color-surface') || '#fff', accent: pick('--color-brand-600') || '#2c70b9' };
}

function attachHost(): void {
  // Only host the data plane in RUN mode (interactive=false) with a token.
  if (props.interactive || props.kind !== 'plugin' || !frameRef.value || !bundleManifest.value) return;
  host = createPluginWidgetHost({
    iframe: frameRef.value,
    bundle: { html: bundleHtml.value, manifest: bundleManifest.value },
    instanceConfig: { config: resolvedConfig.value },
    dataBridge,
    contextProvider,
    tenantId: tenantId.value,
    widgetInstanceId: String(props.p.bundle_id ?? 'plugin'),
  });
  host.start();
}
function teardownHost(): void { stopAllLive(); if (host) { try { host.destroy(); } catch { /* ignore */ } host = null; } }

const srcdoc = computed(() => (props.kind === 'plugin' ? bundleHtml.value : htmlSrc.value));
const phText = computed(() => {
  if (props.kind === 'plugin') return props.p.bundle_id ? 'Cargando widget…' : 'Selecciona un widget bundle';
  return 'Pega HTML en el inspector';
});

onMounted(loadBundle);
watch(() => [props.kind, props.p.bundle_id, props.p.config, props.interactive], loadBundle);
onBeforeUnmount(teardownHost);
</script>

<style scoped>
.syn-embed { width: 100%; height: 100%; overflow: hidden; border-radius: 4px; background: #fff; }
.frame { width: 100%; height: 100%; border: 0; background: transparent; display: block; }
.ph { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; text-align: center; padding: 8px; color: #94a3b8; font-size: .8rem; background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 4px; }
</style>
